// Copyright 2022 Lexoh Inc. All rights reserved.
// NOTICE: All information contained herein is, and remains the property of
// Lexoh Inc. The intellectual and technical concepts contained herein are
// proprietary to Lexoh Inc and may be covered by Canada and Foreign Patents, patents
// in process, and are protected by trade secret or copyright law. Dissemination
// of this information or reproduction of this material is strictly forbidden
// unless prior written permission is obtained from Lexoh Inc. Access to the source
// code contained herein is hereby forbidden to anyone except current Lexoh Inc
// employees, managers or contractors who have executed Confidentiality and
// Non-disclosure agreements explicitly covering such access.

import AuthService from '../services/auth.service';
import EncryptionService from '../services/encryption.service';

async function decryptRole() {
    const storedRole = localStorage.getItem('role');
    if (!storedRole) return null;

    try {
        const decryptedRole = await EncryptionService.decryptText(storedRole, 'Z4gvruT5KndVVrbFQ7JskHzENYag7j6F');
        return JSON.parse(decryptedRole);
    } catch (error) {
        console.error('Error decrypting role:', error);
        return null;
    }
}

async function getInitialState() {
    const storedUser = localStorage.getItem('user');
    const user = storedUser ? JSON.parse(storedUser) : null;

    const role = await decryptRole();

    return {
        status: { loggedIn: !!user },
        user,
        role,
        forgot: null,
    };
}

export const auth = {
    namespaced: true,
    state: {
        status: { loggedIn: false },
        user: null,
        role: null,
        forgot: null,
    },
    actions: {
        async initializeState({ commit }) {
            const initialState = await getInitialState();
            commit('setInitialState', initialState);
        },
        async login({ commit }, user) {
            return AuthService.login(user.username, user.password).then(
                (user) => {
                    commit('loginSuccess', user);
                    return Promise.resolve(user);
                },
                (error) => {
                    commit('loginFailure');
                    return Promise.reject(error);
                }
            );
        },
        async ssoLogin({ commit }, user) {
            return AuthService.ssoLogin(user.code, user.provider, user.state).then(
                (user) => {
                    commit('loginSuccess', user);
                    return Promise.resolve(user);
                },
                (error) => {
                    commit('loginFailure');
                    return Promise.reject(error);
                }
            );
        },
        async logout({ commit }) {
            try {
                await AuthService.logout();
            } catch (error) {
                console.error('Logout error:', error);
            }
            commit('logout');
        },
        register({ commit }, user) {
            return AuthService.register(user).then(
                (user) => {
                    commit('registerSuccess', user);
                    return Promise.resolve(user);
                },
                (error) => {
                    return Promise.reject(error);
                }
            );
        },
        saveFCM({ commit }, fcmToken) {
            return AuthService.saveFCM(fcmToken).then(
                (result) => {
                    commit('saveFCMSuccess', result);
                    return Promise.resolve(result);
                },
                (error) => {
                    commit('saveFCMFailure');
                    return Promise.reject(error);
                }
            );
        },
    },
    mutations: {
        setInitialState(state, initialState) {
            state.status = initialState.status;
            state.user = initialState.user;
            state.role = initialState.role;
            state.forgot = initialState.forgot;
        },
        loginSuccess(state, user) {
            state.status.loggedIn = true;
            state.user = user;
            state.role = user.role;
        },
        loginFailure(state) {
            state.status.loggedIn = false;
            state.user = null;
        },
        logout(state) {
            state.status.loggedIn = false;
            state.user = null;
            state.role = null;
        },
        registerSuccess(state, user) {
            state.status.loggedIn = true;
            state.user = user;
        },
        registerFailure(state) {
            state.status.loggedIn = false;
        },
        saveFCMSuccess(state) {
            // Handle successful FCM save
        },
        saveFCMFailure(state) {
            // Handle failed FCM save
        },
    },
};
